import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { ArrowLeft, Upload } from 'lucide-react';
import { expenses as expensesApi, business, type Department } from '../../lib/api';
import { useAuth } from '../../lib/auth';

interface FromPurchaseRequest {
  id: string;
  department_id: string;
  description: string;
  amount: number;
}

export default function ExpenseSubmit() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const fromPurchaseRequest = (location.state as { fromPurchaseRequest?: FromPurchaseRequest } | null)?.fromPurchaseRequest;
  const needsDepartmentPicker = user?.role === 'director' || user?.role === 'ceo';

  const [departments, setDepartments] = useState<Department[]>([]);
  const [departmentId, setDepartmentId] = useState(fromPurchaseRequest?.department_id || '');
  const [amount, setAmount] = useState(fromPurchaseRequest ? String(fromPurchaseRequest.amount) : '');
  const [description, setDescription] = useState(fromPurchaseRequest?.description || '');
  const [expenseDate, setExpenseDate] = useState(new Date().toISOString().split('T')[0]);
  const [receipt, setReceipt] = useState<File | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (needsDepartmentPicker) {
      business.departments.list().then(({ departments }) => setDepartments(departments)).catch(() => {});
    }
  }, [needsDepartmentPicker]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const parsedAmount = parseFloat(amount);
    if (!parsedAmount || parsedAmount <= 0) { setError('Enter a valid positive amount'); return; }
    if (needsDepartmentPicker && !departmentId) { setError('Choose a department'); return; }

    setLoading(true);
    try {
      const { expense } = await expensesApi.create({
        department_id: needsDepartmentPicker ? departmentId : undefined,
        amount: parsedAmount, description, expense_date: expenseDate,
        purchase_request_id: fromPurchaseRequest?.id,
      });
      if (receipt) {
        await expensesApi.uploadReceipt(expense.id, receipt);
      }
      navigate(`/expenses/${expense.id}`);
    } catch (err: any) {
      setError(err.message || 'Failed to submit expense');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/expenses/mine" className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">Submit Expense</h1>
      </div>

      <div className="card space-y-5">
        {fromPurchaseRequest && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
            Pre-filled from your approved purchase request — this claim skips manager approval and goes straight to Finance.
          </div>
        )}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          {needsDepartmentPicker && (
            <div>
              <label className="label">Department <span className="text-red-500">*</span></label>
              <select className="input-field" value={departmentId} onChange={e => setDepartmentId(e.target.value)} required>
                <option value="">Choose a department...</option>
                {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
          )}

          <div>
            <label className="label">Amount <span className="text-red-500">*</span></label>
            <input
              type="number" step="0.01" min="0.01" className="input-field" placeholder="0.00"
              value={amount} onChange={e => setAmount(e.target.value)} required
            />
          </div>

          <div>
            <label className="label">Description <span className="text-gray-400 font-normal">(optional)</span></label>
            <input
              type="text" className="input-field" placeholder="What was this for?"
              value={description} onChange={e => setDescription(e.target.value)} maxLength={200}
            />
          </div>

          <div>
            <label className="label">Date <span className="text-red-500">*</span></label>
            <input type="date" className="input-field" value={expenseDate} onChange={e => setExpenseDate(e.target.value)} required />
          </div>

          <div>
            <label className="label">Receipt <span className="text-gray-400 font-normal">(optional)</span></label>
            <label className="flex items-center gap-2 border border-dashed border-gray-300 rounded-lg px-4 py-3 cursor-pointer hover:border-green-400 transition-colors">
              <Upload className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-500">{receipt ? receipt.name : 'Click to attach a receipt image or PDF'}</span>
              <input type="file" accept="image/*,application/pdf" className="hidden" onChange={e => setReceipt(e.target.files?.[0] || null)} />
            </label>
          </div>

          <div className="flex gap-3 pt-2">
            <Link to="/expenses/mine" className="flex-1 btn-secondary text-center">Cancel</Link>
            <button type="submit" className="flex-1 btn-primary" disabled={loading}>
              {loading ? 'Submitting...' : 'Submit Expense'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
